// src/App.js - Pi 로그인 시 익명 인증 스킵으로 비용 최적화
// [네트워크 개선] 적응형 타임아웃 적용으로 느린 네트워크 대응
// 🎯 [Stage 2 수정] 환경 정보 중앙화 - AppProvider에 environment prop 전달
// 🔧 [실행 순서 개선] 환경 확정 후 점검/인증 시작
// 🔧 [Pi 동의 화면 대기 개선] Pi Browser에서 인증 실패 시 자동 재시도

import React, { Suspense, useState, useEffect, useRef } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { AppProvider } from './contexts/AppContext';
import { MinigameProvider } from './contexts/MinigameContext';
import { saveUser } from './services/api';
// [추가] Firestore 직접 읽기를 위한 import
import { db, auth } from './firebase'; // 🔥 [Auth] auth 추가
import { Toaster } from 'react-hot-toast'; // 🎯 [New] Toast Notification
import { doc, getDoc } from 'firebase/firestore';
import { signInWithCustomToken, onAuthStateChanged } from 'firebase/auth'; // 🔥 [Auth] Custom Token + onAuthStateChanged (세션 복구 최적화)
import { initializeRemoteConfig } from './services/remoteConfig'; // [추가] Remote Config import
// Analytics 제거됨 - Firebase Analytics 사용 안함
import { useTranslation } from 'react-i18next';
// [멀티 플랫폼 지원] 환경 감지 함수들 import
import { getPlatform, getEnvironment, getEnvironmentInfo, refreshEnvironment, debugLog, maskSensitiveData, isPiBrowserEnvironment, isPiSDKReady } from './utils/environment';
import { handleIncompletePayment } from './utils/paymentRecovery'; // 🎯 [결제 복구] 미완료 결제 즉시 처리
// 🔧 [iOS Safari Fix] getEnvironment를 컴포넌트 내부에서도 사용 가능하도록 import 확인됨
import { GamePlayProvider } from './contexts/GamePlayContext';
import { ThemeDownloadProvider } from './contexts/ThemeDownloadContext';
import { DonationProvider } from './contexts/DonationContext';
import { CityEquippedCacheProvider } from './contexts/CityEquippedCacheContext'; // 🎯 [CityCache] 추가
import PiAdapter from './adapters/PiAdapter'; // 🎯 [SDK Init Guard] 초기화 보장용

import { SPLASH_MAX_MS, getBootView, hasStoredPiSession } from './utils/bootGate';
import SplashScreen from './pages/SplashScreen';
import Main from './pages/Main';
import TosAgreementModal from './components/common/TosAgreementModal'; // 🎯 [ToS] 약관 동의 모달
import LoadingOverlay from './components/common/LoadingOverlay'; // 🎯 [UX] 글로벌 로딩 오버레이
import ErrorBoundary from './components/common/ErrorBoundary'; // 🎯 [iOS Fix] 에러 발생 시 빈 화면 방지

// 🔧 [개발용] 테스트 데이터 시드 유틸리티 (브라우저 콘솔: seedTestVotes(), seedTestDeals())
// production 빌드에서는 번들에서 완전히 제외됨 (Pi 정책: 운영 빌드에 테스트 흔적 금지)
if (process.env.NODE_ENV !== 'production') {
  require('./utils/seedVotes');
  require('./utils/seedDeals');
}

// 미니게임은 동적 로드로 분리
const MinigameRouter = React.lazy(() => import('./pages/minigame'));
// 첫 화면에 필요하지 않은 경로는 방문할 때만 내려받는다.
const GameResult = React.lazy(() => import('./pages/GameResult'));
const GameWrapper = React.lazy(() => import('./pages/GameWrapper'));
const DonationRegister = React.lazy(() => import('./pages/DonationRegister'));
const SellerDashboard = React.lazy(() => import('./components/seller/SellerDashboard'));
const AdminPage = React.lazy(() => import('./pages/admin/AdminPage.js'));

// Loading component with translation
function LoadingScreen() {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: '#f0f2f5' }}>
      <h1 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#333', margin: '0 0 16px 0' }}>Spot Go</h1>
      <p style={{ fontSize: '0.85rem', color: '#888', margin: 0 }}>{t('checking_environment')}</p>
    </div>
  );
}

// Minigame loading component with translation
function MinigameLoadingScreen() {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#1a1a2e' }}>
      <div style={{ color: 'white', fontSize: '1.2em' }}>{t('minigame_loading')}</div>
    </div>
  );
}

function App() {
  const { t } = useTranslation(); // [수정] useTranslation 추가 - 점검 모드 메시지용
  const bootStartRef = useRef(Date.now());
  const hadSessionAtStartRef = useRef(hasStoredPiSession(typeof sessionStorage !== 'undefined' ? sessionStorage : null));
  const [bootElapsedMs, setBootElapsedMs] = useState(0);
  const [mapReady, setMapReady] = useState(false);
  const [authState, setAuthState] = useState('initializing'); // 🎯 [근본 해결] 초기 상태를 initializing으로 변경
  const [piUser, setPiUser] = useState(() => {
    try {
      if (!hadSessionAtStartRef.current) return null;
      const uid = sessionStorage.getItem('pi-user-id');
      if (!uid) return null;
      return { uid, username: sessionStorage.getItem('pi-username') || '' };
    } catch (e) {
      return null;
    }
  });

  // [멀티 플랫폼 지원] 플랫폼 및 환경 정보 저장
  const [platform] = useState(() => getPlatform());
  // 🔧 [실행 순서 개선] 환경 초기값을 null로 변경, 확정 상태 추가
  const [piEnvironment, setPiEnvironment] = useState(null);
  const [isEnvReady, setIsEnvReady] = useState(false);

  // ✅ [Admin] 관리자 모드 감지 - state로 관리하여 hash 변경 추적
  // 도메인이 admin.painori.com 이거나, hash에 mode=admin이 있으면 관리자 모드
  const isDomainAdmin = window.location.hostname === 'admin.painori.com';
  const [isAdminMode, setIsAdminMode] = useState(isDomainAdmin || window.location.hash.includes('mode=admin'));

  // ✅ [Admin] hash 변경 감지
  useEffect(() => {
    const checkAdminMode = () => {
      const adminHash = window.location.hash.includes('mode=admin');
      const adminDomain = window.location.hostname === 'admin.painori.com';
      const isActive = adminHash || adminDomain;

      console.log('[App] Admin 모드 감지:', isActive, 'hash:', window.location.hash, 'domain:', window.location.hostname);
      setIsAdminMode(isActive);
    };

    // 초기 체크
    checkAdminMode();

    // hash 변경 감지
    window.addEventListener('hashchange', checkAdminMode);
    return () => window.removeEventListener('hashchange', checkAdminMode);
  }, []);

  // [추가] Pi 인증 실패 상태 추가 - testspot 문제 해결용
  const [piAuthFailed, setPiAuthFailed] = useState(false);

  // [추가] 점검 모드 상태
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [isCheckingMaintenance, setIsCheckingMaintenance] = useState(true);

  // 🔧 [iOS Safari Fix] iOS Safari 감지
  const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  // 🔧 [Pi 동의 화면 대기 개선] 재시도 타이머 ref 추가
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const authInFlightRef = useRef(null);

  // 🔧 [iOS Safari Fix] 강제 렌더링 상태
  const [forceRender, setForceRender] = useState(false);

  // [추가] 뒤로 가기 방지 로직 (Double Back to Exit) - 최상단 배치로 훅 순서 보장
  const [showExitToast, setShowExitToast] = useState(false);
  const backPressTimerRef = useRef(null);

  // 🎯 [UX] 글로벌 로딩 상태
  const [loadingOverlay, setLoadingOverlay] = useState({ show: false, message: '' });

  useEffect(() => {
    // 1. 초기 History State 추가 (뒤로 가기 방지용 버퍼)
    if (window.history.state === null) {
      window.history.pushState(null, '', window.location.href);
    }

    const handlePopState = () => {
      // 0. 모달이 열려있는 경우 (DOM 기반 감지)
      if (document.querySelector('[data-modal-open="true"]')) {
        // 모달이 열려있으면 App.js의 종료 로직 무시
        // 하위 컴포넌트가 popstate를 감지하여 닫거나, 그냥 네비게이션이 발생함
        return;
      }

      // 2. 뒤로 가기 감지 시 실행
      if (backPressTimerRef.current) {
        // 종료 허용
        clearTimeout(backPressTimerRef.current);
        backPressTimerRef.current = null;
        setShowExitToast(false);
        return;
      }

      // 3. 첫 번째 클릭: 종료 방지 및 안내 메시지
      setShowExitToast(true);
      setTimeout(() => setShowExitToast(false), 2000);

      // 종료 방지 핵심: 다시 상태 push
      window.history.pushState(null, '', window.location.href);

      backPressTimerRef.current = setTimeout(() => {
        backPressTimerRef.current = null;
      }, 2000);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (backPressTimerRef.current) clearTimeout(backPressTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const onReady = () => setMapReady(true);
    window.addEventListener('painori-spot-boot-ready', onReady);
    const remain = SPLASH_MAX_MS - (Date.now() - bootStartRef.current);
    const timer = setTimeout(() => setBootElapsedMs(SPLASH_MAX_MS), Math.max(0, remain));
    return () => {
      window.removeEventListener('painori-spot-boot-ready', onReady);
      clearTimeout(timer);
    };
  }, []);

  const bootView = getBootView({
    elapsedMs: Math.max(bootElapsedMs, Date.now() - bootStartRef.current),
    authState,
    hadSession: hadSessionAtStartRef.current,
    mapReady
  });
  const showSplash = isAdminMode ? false : bootView.showSplash;
  const bootCover = isAdminMode ? false : bootView.bootCover;

  // [최적화] 조건부 로깅 함수로 변경
  const addDebugLog = (message, data = null) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}${data ? ': ' + JSON.stringify(maskSensitiveData(data)) : ''}`;
    debugLog(logEntry);
  };

  // 🔧 [실행 순서 개선] 환경 결정 전용 useEffect - 가장 먼저 실행
  useEffect(() => {
    const determineEnvironment = async () => {
      // [Admin] 관리자 모드면 환경 체크 스킵 - sandbox로 설정
      if (isAdminMode) {
        addDebugLog('[App] Admin 모드 - 환경 체크 스킵');
        setPiEnvironment('sandbox');
        setIsEnvReady(true);
        setIsCheckingMaintenance(false);
        return;
      }

      addDebugLog('[App] 🔧 환경 결정 프로세스 시작');

      // 🔧 [iOS Safari Fix] 타임아웃 설정 - 무한 대기 방지
      // 주의: 클로저 문제를 피하기 위해 ref 사용 대신 직접 상태 체크
      let envResolved = false;
      const envTimeout = setTimeout(() => {
        if (!envResolved) {
          addDebugLog('[App] ⚠️ 환경 결정 타임아웃 - getEnvironment() 기본값으로 진행');
          const fallbackEnv = getEnvironment(); // getEnvironment()는 항상 값을 반환함 (최종 폴백: 'sandbox')
          setPiEnvironment(fallbackEnv);
          setIsEnvReady(true);
          envResolved = true;
        }
      }, isIOSSafari ? 8000 : 15000);

      // 초기 환경 값 가져오기
      let initialEnv = getEnvironment();
      addDebugLog('[App] 초기 환경 값:', initialEnv);

      // Pi SDK 로드 대기 (iOS Safari는 더 짧은 시간)
      let waitCount = 0;
      const maxWait = isIOSSafari ? 10 : 20; // iOS Safari: 1초, 그 외: 2초 (Pi Browser는 window.Pi 이미 존재 → 첫 루프에서 즉시 탈출)

      while (waitCount < maxWait) {
        if (window.Pi?.environment) {
          addDebugLog(`[App] Pi SDK 감지됨 (${waitCount * 100}ms 후)`);
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        waitCount++;
      }

      // 최종 환경 결정
      let finalEnv;
      if (window.Pi?.environment) {
        // Pi SDK가 있으면 refreshEnvironment로 최신 값 가져오기
        // Pi SDK는 'sandbox' 또는 'production'만 반환 (testnet은 sandbox로 취급)
        finalEnv = refreshEnvironment();
        addDebugLog('[App] Pi SDK 기반 환경 값:', finalEnv);
      } else {
        // Pi SDK가 없으면 초기 값 사용
        // getEnvironment()는 항상 값을 반환함 (최종 폴백: 'sandbox')
        finalEnv = initialEnv;
        addDebugLog('[App] Pi SDK 없음 - 초기 환경 값 사용:', finalEnv);
      }

      // 환경 설정 및 준비 완료 표시
      envResolved = true;
      clearTimeout(envTimeout);
      setPiEnvironment(finalEnv);
      setIsEnvReady(true);

      // 🔍 [디버깅 추가] 애널리틱스 상태 확인 (제거됨 - Firebase Analytics 사용 안함)
      // setTimeout(() => {
      //   checkAnalyticsStatus();
      // }, 2000); // 2초 후 확인하여 스크립트 로드 완료 보장

      addDebugLog('[App] 🔧 환경 결정 완료:', {
        최종환경: finalEnv,
        SDK존재: !!window.Pi,
        소요시간: `${waitCount * 100}ms`,
        iOS: isIOSSafari
      });
    };

    determineEnvironment();

    return () => {
      // Cleanup은 determineEnvironment 내부에서 처리됨
    };
  }, [isIOSSafari]); // 🔧 iOS Safari 감지 의존성 추가



  // 🎯 [Session Restore] 앱 재시작/새로고침 시 세션 복구 logic
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const savedUid = sessionStorage.getItem('pi-user-id');
        const savedUsername = sessionStorage.getItem('pi-username');

        // 게스트 상태였다는 플래그 확인
        const wasGuest = sessionStorage.getItem('is-guest-mode');

        if (savedUid && savedUsername) {
          const recoveredUser = {
            uid: savedUid,
            username: savedUsername,
            accessToken: sessionStorage.getItem('pi-access-token')
          };

          addDebugLog('🔄 [Session] 기존 로그인 세션 복구 시도', { username: savedUsername });

          // 🎯 [성능 최적화] Firebase Auth 상태 먼저 확인 → 유효하면 API 재호출 스킵
          if (piEnvironment) {
            // onAuthStateChanged: Firebase 초기화 완전 완료 후 세션 상태 1회 확인 (Promise 래핑)
            // - 유효 세션 있음: saveUser API + signInWithCustomToken 완전 스킵 → 즉시 복구
            // - 유효 세션 없음: 기존 saveUserToFirebase 방식으로 폴백 (동작 변화 없음)
            const firebaseUser = await new Promise((resolve) => {
              const unsubscribe = auth.onAuthStateChanged((user) => {
                unsubscribe(); // 1회 확인 후 즉시 해제
                resolve(user);
              });
            });

            if (firebaseUser && firebaseUser.uid === savedUid) {
              // ⚡ Firebase 세션 유효 + UID 일치 → API/토큰 재발급 없이 즉시 복구
              addDebugLog('⚡ [Session] Firebase 세션 유효 - 즉시 복구 (saveUser API 스킵)');
              setPiUser(recoveredUser);
              setAuthState('authenticated');
            } else {
              // 🔄 Firebase 세션 없음 또는 UID 불일치 → 기존 방식으로 재인증 (안전 폴백)
              addDebugLog(`🔄 [Session] Firebase 재인증 필요 (reason: ${firebaseUser ? 'uid mismatch' : 'no session'})`);
              await saveUserToFirebase(recoveredUser, recoveredUser.accessToken).then((success) => {
                if (success) {
                  setPiUser(recoveredUser);
                  setAuthState('authenticated');
                  addDebugLog('✅ [Session] 세션 복구 및 Firebase 재인증 완료');
                } else {
                  addDebugLog('❌ [Session] 세션 복구 실패 (Firebase Auth Failed)');
                  // 실패 시 게스트 모드로 잔류하거나 재로그인 유도
                  // 여기서는 아무것도 안하면 초기상태(Guest/Initializing) 유지됨
                }
              }).catch(err => {
                console.error("Session restore firebase auth failed", err);
              });
            }
          } else {
            // 환경변수 로드 전이라도 일단 복구 (useEffect[piEnvironment]가 없으므로 이 케이스는 드묾)
            setPiUser(recoveredUser);
            setAuthState('authenticated');
          }

        } else if (wasGuest) {
          addDebugLog('🔄 [Session] 기존 게스트 세션 확인됨');
        }
      } catch (e) {
        addDebugLog('⚠️ [Session] 세션 복구 중 오류', e);
      }
    };

    // piEnvironment가 준비된 경우에만 복구 시도 (또는 내부에서 체크)
    if (piEnvironment) {
      restoreSession();
    }
  }, [piEnvironment]); // 의존성 추가: 환경설정 완료 후 실행

  // [추가] SpotGoMain에서 발생하는 수동 로그인 성공 이벤트 위임 처리
  useEffect(() => {
    const handleLoginSuccess = (event) => {
      const user = event.detail;
      addDebugLog('✅ 수동 로그인 성공 이벤트 수신', maskSensitiveData(user));

      // 🎯 [Race Condition Fix] Firebase 로그인 완료 후 상태 변경 (Sequential Guard)
      // UI 렌더링(TosAgreementModal)을 막기 위해 Firebase Auth가 끝날 때까지 State 업데이트 보류
      if (piEnvironment) {
        saveUserToFirebase(user, user && user.accessToken).then((success) => {
          if (success) {
            setPiUser(user);
            setAuthState('authenticated');
          } else {
            console.error("Firebase save failed in listener - Fail Closed");
            // 🎯 [Fail-Closed] 인증 실패 시 절대 진입 금지
            // 사용자에게 피드백 제공 (선택사항)
            alert("서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.");
          }
        }).catch(err => {
          console.error("Firebase save unexpected error", err);
          alert("인증 오류가 발생했습니다.");
        });
      } else {
        // 환경설정이 아직 안된 경우? 보통 이 시점엔 되어있음.
        setPiUser(user);
        setAuthState('authenticated');
      }
    };

    window.addEventListener('pi-login-success', handleLoginSuccess);
    return () => window.removeEventListener('pi-login-success', handleLoginSuccess);
  }, [piEnvironment]); // piEnvironment가 설정된 후 리스너 등록

  // 🎯 [Moderation] 강제 게스트 전환 이벤트 처리 (정지된 유저)
  useEffect(() => {
    const handleForceGuest = (event) => {
      const { reason } = event.detail || {};
      addDebugLog('🚫 사용자 정지 감지 - 강제 게스트 전환', { reason });

      // 1. 알림 표시 (다국어 지원)
      // t 함수가 없는 경우를 대비한 안전장치
      const message = t('account_suspended_desc', { reason: reason || 'Violation of terms' });
      alert(message);

      // 2. 로그아웃 및 게스트 전환
      setPiUser(null);
      setAuthState('guest');
      sessionStorage.clear();
      sessionStorage.setItem('user-type', 'guest');

      // 3. UI 업데이트 강제 (선택사항)
    };

    window.addEventListener('force-guest-mode', handleForceGuest);
    return () => window.removeEventListener('force-guest-mode', handleForceGuest);
  }, [t]);


  const saveUserToFirebase = async (user, accessToken) => {
    try {
      // 🎯 [UX] 글로벌 로딩 시작
      setLoadingOverlay({ show: true, message: t('loading') });

      // 🎯 [Auth] 서버에서 Custom Token을 받아옴
      const response = await saveUser(user.uid, user.username, piEnvironment, accessToken);

      if (response && response.customToken) {
        addDebugLog('🔐 [Auth] Custom Token 수신 - Firebase 로그인 진행');
        await signInWithCustomToken(auth, response.customToken);
        addDebugLog('✅ [Auth] Firebase 로그인 완료 (권한 획득)');
      } else {
        // 🎯 [Fail-Closed] Custom Token이 없으면 인증 실패로 간주
        addDebugLog('❌ [Auth] Custom Token 미수신 (서버 설정 또는 권한 문제)');
        return false;
      }

      addDebugLog('User saved to Firebase successfully');
      return true; // 🎯 [Fail-Closed] 성공 시 true 반환

    } catch (error) {
      addDebugLog('Failed to save user to Firebase', error.message);
      return false; // 🎯 [Fail-Closed] 실패 시 false 반환
    } finally {
      // 🎯 [UX] 글로벌 로딩 종료 (성공이든 실패든)
      setLoadingOverlay({ show: false, message: '' });
    }
  };

  /**
   * [네트워크 개선] 네트워크 상태에 따른 적응형 타임아웃 계산
   */
  const getAdaptiveTimeout = (attempt) => {
    // Navigator.connection API 지원 확인
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const effectiveType = connection?.effectiveType;
    const downlink = connection?.downlink; // Mbps 단위

    addDebugLog(`네트워크 상태 감지`, {
      effectiveType,
      downlink: downlink ? `${downlink} Mbps` : 'unknown',
      rtt: connection?.rtt ? `${connection.rtt}ms` : 'unknown'
    });

    // 네트워크 상태별 기본 타임아웃 설정
    let baseTimeout;
    if (effectiveType) {
      const timeoutMap = {
        '4g': 5000,      // 5초 - 빠른 네트워크
        '3g': 10000,     // 10초 - 보통 네트워크
        '2g': 20000,     // 20초 - 느린 네트워크
        'slow-2g': 30000 // 30초 - 매우 느린 네트워크
      };
      baseTimeout = timeoutMap[effectiveType] || 10000;
    } else if (downlink) {
      // downlink 속도 기반 타임아웃 (Mbps)
      if (downlink > 10) baseTimeout = 5000;      // 빠름
      else if (downlink > 2) baseTimeout = 10000; // 보통
      else if (downlink > 0.5) baseTimeout = 20000; // 느림
      else baseTimeout = 30000; // 매우 느림
    } else {
      // 네트워크 정보가 없으면 기본값 사용
      baseTimeout = 10000;
    }

    // 시도 횟수에 따른 점진적 증가 (최대 30초)
    const timeout = Math.min(baseTimeout * (1 + (attempt - 1) * 0.5), 30000);

    addDebugLog(`적응형 타임아웃 계산`, {
      시도: attempt,
      기본타임아웃: baseTimeout,
      최종타임아웃: timeout
    });

    return timeout;
  };

  /**
   * [네트워크 개선] Pi 인증 with 적응형 타임아웃
   * Promise.race를 사용하여 타임아웃 구현
   */
  const authenticateWithTimeout = async (scopes, onIncompletePaymentFound, timeoutMs = 10000) => {
    const piAuthPromise = window.Pi.authenticate(scopes, onIncompletePaymentFound);
    // timeout 발생 후 실제 인증 Promise가 늦게 reject 되더라도 unhandled rejection 방지
    piAuthPromise.catch(() => {});

    let timeoutId = null;
    const timeoutPromise = new Promise((resolve) => {
      timeoutId = setTimeout(() => {
        resolve({ status: 'timeout', pendingPromise: piAuthPromise });
      }, timeoutMs);
    });

    try {
      const raceResult = await Promise.race([
        piAuthPromise.then((authResult) => ({ status: 'success', authResult })),
        timeoutPromise
      ]);
      return raceResult;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  };

  /**
   * 🔧 [Pi 동의 화면 대기 개선] Pi 인증 재시도 함수 - 적응형 타임아웃 적용
   * Pi Browser에서는 무한 재시도, 일반 웹에서는 3회만 시도
   */
  const simplePiAuthentication = async (maxRetries = null, timeoutOverride = null) => {
    if (authInFlightRef.current) {
      addDebugLog('[Pi Auth] 인증 진행 중 - 기존 요청 재사용');
      return authInFlightRef.current;
    }

    const authTask = (async () => {
    const isPiBrowser = isPiBrowserEnvironment();
    const actualMaxRetries = maxRetries !== null ? maxRetries : (isPiBrowser ? Infinity : 3);

    // 재시도 카운터 증가
    retryCountRef.current++;
    const currentAttempt = retryCountRef.current;

    addDebugLog(`Pi 인증 시도 ${currentAttempt}${actualMaxRetries !== Infinity ? '/' + actualMaxRetries : ''}`);

    try {
      // SDK 준비 상태 확인
      if (!window.Pi || !window.Pi.authenticate) {
        throw new Error('Pi SDK not ready - authenticate method missing');
      }

      // 🎯 [NEW] PiAdapter를 통한 초기화 보장 (SDK 지연 로드 시 init 미호출 방지)
      if (!PiAdapter.isInitialized()) {
        addDebugLog('[Pi Auth] SDK 미초기화 감지 - 자동 초기화 시도');
        const initOk = PiAdapter.ensureInitialized();
        if (!initOk) {
          throw new Error('Pi SDK initialization failed - please restart the app');
        }
      }

      const scopes = ['username', 'payments', 'wallet_address'];

      // [네트워크 개선] 적응형 타임아웃 적용 (오버라이드 있으면 우선 사용)
      const timeout = timeoutOverride || getAdaptiveTimeout(currentAttempt);

      // 🎯 [결제 복구] onIncompletePaymentFound: Pi SDK가 미완료 결제를 감지하면 즉시 서버에 /complete 호출
      // (이전 코드는 sessionStorage에 저장만 하고 서버 호출을 안 해서 차단이 영구화되는 버그가 있었음)
      const currentEnvironment = getEnvironment();
      const authAttempt = await authenticateWithTimeout(
        scopes,
        async (payment) => {
          addDebugLog("Incomplete payment detected - attempting immediate recovery", payment?.identifier);
          await handleIncompletePayment(payment, currentEnvironment);
        },
        timeout
      );

      let authResult = authAttempt?.authResult;
      if (authAttempt?.status === 'timeout') {
        const graceTimeout = isPiBrowser ? 20000 : 10000;
        addDebugLog(`Pi 인증 1차 타임아웃 - 내부 Grace 대기 시작 (${graceTimeout}ms)`);

        authResult = await Promise.race([
          authAttempt.pendingPromise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Pi authentication timeout')), graceTimeout)
          )
        ]);
      }

      if (authResult && authResult.user) {
        addDebugLog(`✅ Pi 인증 성공 (시도 ${currentAttempt})`, maskSensitiveData(authResult.user));

        // 재시도 타이머 정리
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }
        retryCountRef.current = 0;

        // Analytics 제거됨 - Firebase Analytics 사용 안함
        addDebugLog('[Pi] Pi 사용자 인증 완료');

        // 🎯 [Race Condition Fix] Firebase 로그인 완료 후 상태 변경 (Sequential Guard)
        // 이 순서가 매우 중요함: Firebase Auth 완료 -> UI 렌더링
        // 🎯 [Race Condition Fix] Firebase 로그인 완료 후 상태 변경 (Sequential Guard)
        // 이 순서가 매우 중요함: Firebase Auth 완료 -> UI 렌더링
        const isSuccess = await saveUserToFirebase(authResult.user, authResult.accessToken);

        if (isSuccess) {
          setPiUser(authResult.user);
          sessionStorage.setItem('pi-username', authResult.user.username);
          sessionStorage.setItem('pi-user-id', authResult.user.uid);
          sessionStorage.setItem('pi-access-token', authResult.accessToken);
          sessionStorage.setItem('user-type', 'pi');

          setAuthState('authenticated');
          setPiAuthFailed(false);
          return true;
        } else {
          throw new Error('Firebase authentication failed (Fail-Closed)');
        }
      } else {
        throw new Error('No authentication result');
      }

    } catch (error) {
      addDebugLog(`❌ Pi 인증 실패 (시도 ${currentAttempt})`, {
        message: error.message,
        isTimeout: error.message.includes('timeout'),
        isNetworkError: error.message.includes('NetworkError') || error.message.includes('Failed to fetch'),
        isCancelled: error.message.includes('cancelled') || error.message.includes('declined')
      });

      // 🎯 [간소화] 자동 재시도 제거 - 1회 시도 후 게스트 상태 유지
      addDebugLog('Pi 인증 실패 - 게스트 상태 유지 (재시도 없음)');
      // 게스트 상태는 이미 기본값이므로 추가 작업 불필요
    }

    // 모든 시도 실패 (일반 웹에서만)
    addDebugLog(`❌ ${actualMaxRetries}회 시도 모두 실패`);
    retryCountRef.current = 0;
    return false;
    })();

    authInFlightRef.current = authTask;
    try {
      return await authTask;
    } finally {
      if (authInFlightRef.current === authTask) {
        authInFlightRef.current = null;
      }
    }
  };

  // 🔧 [실행 순서 개선] 인증 로직 - 환경 준비 완료 후 실행
  useEffect(() => {
    // 환경이 준비되지 않았으면 실행하지 않음
    if (!isEnvReady || !piEnvironment) {
      addDebugLog('[App] 환경 미확정 - 인증 대기');
      return;
    }

    // [최적화] 환경 정보 로깅 - 개발 환경에서만
    const envInfo = getEnvironmentInfo();
    addDebugLog("=== Environment Details ===");
    Object.entries(envInfo).forEach(([key, value]) => {
      addDebugLog(`${key}: ${value}`);
    });

    const initAuth = async () => {
      addDebugLog('[App] initAuth started');
      addDebugLog('[App] Current platform:', platform);
      addDebugLog('[App] Pi environment:', piEnvironment);

      // [2단계 개선] 환경 초기화 완료 대기 - 더 긴 시간 허용
      if (!window.__APP_ENVIRONMENT__?.initialized) {
        addDebugLog('[App] Waiting for environment initialization...');
        let waitCount = 0;
        while (!window.__APP_ENVIRONMENT__?.initialized && waitCount < 30) { // 3초로 연장
          await new Promise(resolve => setTimeout(resolve, 100));
          waitCount++;
        }

        if (!window.__APP_ENVIRONMENT__?.initialized) {
          addDebugLog('[App] Environment initialization timeout - proceeding with defaults');
        }
      }

      // [근본적 개선] 환경 정보 재확인
      const currentPlatform = window.__APP_ENVIRONMENT__?.platform || platform;
      const sdkLoaded = window.__APP_ENVIRONMENT__?.piSDKLoaded || false;
      const sdkReady = window.__APP_ENVIRONMENT__?.piSDKReady || false;

      // 환경은 이미 확정됨
      const currentEnvironment = piEnvironment;

      addDebugLog('[App] Environment initialization complete:', {
        platform: currentPlatform,
        piSDKLoaded: sdkLoaded,
        piSDKReady: sdkReady,
        piEnvironment: currentEnvironment
      });

      // [추가] Remote Config 초기화
      try {
        addDebugLog('[App] Initializing Remote Config...');
        await initializeRemoteConfig();
        addDebugLog('[App] Remote Config initialized successfully');
      } catch (error) {
        addDebugLog('[App] Remote Config initialization failed:', error.message);
        // Remote Config 실패해도 앱은 계속 실행
      }

      // [보안 강화] Pi Browser 환경 확인
      const isPiBrowser = isPiBrowserEnvironment();
      addDebugLog('[App] Pi Browser 환경 여부:', isPiBrowser);

      // [멀티 플랫폼 지원 - 근본적 개선] 플랫폼별 처리
      if (currentPlatform === 'web' && !sdkLoaded && !isPiBrowser) {
        // 🎯 [간소화] 일반 웹 환경 - 게스트 상태 유지, 메인 화면 바로 표시
        addDebugLog("🌐 일반 웹 환경 - 게스트 상태로 진입");
        // 🎯 [핵심] user-type을 guest로 설정 - isGuest() 함수가 정상 동작하도록
        sessionStorage.setItem('user-type', 'guest');
        setAuthState('guest'); // 🎯 [근본 해결] 명시적 게스트 전환
        return;
      }

      // ===== Pi 플랫폼 또는 Pi Browser 환경 처리 =====
      if (currentPlatform === 'pi' || sdkLoaded || isPiBrowser) {
        addDebugLog('[App] Pi 인증 가능 환경 - 자동 로그인 시도');

        // Pi SDK 확인
        if (!window.Pi) {
          // 🎯 [간소화] 게스트 상태 유지
          addDebugLog("❌ Pi SDK not found - 게스트 상태로 진입");
          // 🎯 [핵심] user-type을 guest로 설정
          sessionStorage.setItem('user-type', 'guest');
          setAuthState('guest'); // 🎯 [근본 해결] 명시적 게스트 전환
          return;
        }

        // Pi SDK 상태 출력 - 민감한 정보 제외
        addDebugLog('Pi SDK status:', {
          exists: !!window.Pi,
          ready: isPiSDKReady(),
          initialized: window.Pi?.initialized,
          authenticated: window.Pi?.authenticated,
          environment: window.Pi?.environment,
          methods: window.Pi ? Object.keys(window.Pi) : []
        });

        // 세션 확인 - 민감한 정보 마스킹
        const savedUsername = sessionStorage.getItem('pi-username');
        const savedUserId = sessionStorage.getItem('pi-user-id');
        addDebugLog("Session check", maskSensitiveData({ savedUsername, savedUserId }));

        // Pi 환경 확인
        const isSandbox = currentEnvironment === 'sandbox';
        addDebugLog("Pi environment", { piEnvironment: currentEnvironment, isSandbox });

        // 🎯 [간소화] Pi Browser에서도 1회만 시도 - 백그라운드 처리
        if (isPiBrowser) {
          addDebugLog("🚀 Pi Browser 환경 - 백그라운드 인증 1회 시도");

          // 🎯 [핵심] await 없이 백그라운드로 실행 - 메인 화면 즉시 표시
          const piBrowserTimeout = Math.max(getAdaptiveTimeout(1), 15000);
          addDebugLog(`Pi Browser 자동 로그인 타임아웃 상향`, { timeout: piBrowserTimeout });
          simplePiAuthentication(1, piBrowserTimeout).then((authSuccess) => {
            if (authSuccess) {
              addDebugLog("✅ Pi Browser 인증 성공 - 상태 업데이트됨");
              // 상태는 simplePiAuthentication 내부에서 이미 업데이트됨
            } else {
              addDebugLog("❌ Pi Browser 인증 실패 - 게스트 상태 유지");
              setAuthState('guest'); // 🎯 [Fix] 인증 실패 시 명시적 게스트 전환
            }
          }).catch((error) => {
            addDebugLog("❌ Pi Browser 인증 에러 - 게스트 상태 유지", error.message);
            setAuthState('guest'); // 🎯 [근본 해결] 명시적 게스트 전환
          });
          // await가 없으므로 즉시 다음 코드로 진행 (게스트 상태로 메인 화면 표시)
          return;

        } else {
          // 일반 환경에서도 네트워크 상태 고려
          try {
            addDebugLog("🚀 일반 환경 Pi 인증 시도");

            // [2단계 개선] SDK 준비 상태 확인
            if (!isPiSDKReady()) {
              throw new Error('Pi SDK not ready - methods not available');
            }

            const scopes = ['username', 'payments', 'wallet_address'];

            // [네트워크 개선] 일반 환경에서도 적응형 타임아웃 적용
            const timeout = getAdaptiveTimeout(1);

            const authAttempt = await authenticateWithTimeout(
              scopes,
              (payment) => {
                addDebugLog("Incomplete payment detected", payment);
                if (payment) {
                  sessionStorage.setItem('pi-incomplete-payment', JSON.stringify(payment));
                }
              },
              timeout
            );

            let authResult = authAttempt?.authResult;
            if (authAttempt?.status === 'timeout') {
              addDebugLog('일반 환경 Pi 인증 타임아웃');
              throw new Error('Pi authentication timeout');
            }

            addDebugLog("Pi authentication result", authResult ? 'success' : 'failed');

            if (authResult && authResult.user) {
              // 🎯 [Race Condition Fix] Firebase 로그인 완료 후 상태 변경 (Sequential Guard)
              const isSuccess = await saveUserToFirebase(authResult.user, authResult.accessToken);

              if (isSuccess) {
                // 🎯 [Fail-Closed Fix] 검증 성공 시에만 유저 정보 및 세션 저장
                // 기존에는 검증 전에 저장하여 에러 시에도 UI가 노출되는 문제가 있었음
                addDebugLog("✅ Pi authentication success", maskSensitiveData(authResult.user));
                setPiUser(authResult.user);
                sessionStorage.setItem('pi-username', authResult.user.username);
                sessionStorage.setItem('pi-user-id', authResult.user.uid);
                sessionStorage.setItem('pi-access-token', authResult.accessToken);
                sessionStorage.setItem('user-type', 'pi');

                setAuthState('authenticated');
                setPiAuthFailed(false);
              } else {
                throw new Error('Firebase authentication failed (Fail-Closed)');
              }
            } else {
              throw new Error('No authentication result');
            }

          } catch (error) {
            addDebugLog("❌ 일반 환경 Pi 인증 실패", {
              message: error.message,
              stack: error.stack
            });

            // 🎯 [간소화] 게스트 상태 유지
            setPiAuthFailed(true);
            sessionStorage.clear();
            // 🎯 [핵심] user-type을 guest로 설정 (clear 후 재설정)
            sessionStorage.setItem('user-type', 'guest');
            setAuthState('guest'); // 🎯 [근본 해결] 명시적 게스트 전환
          }
        }
      }
    };

    // [2단계 개선] 초기화 타이밍 조정 - 환경 초기화 완료 후 실행
    const delay = 100; // 스플래시 화면과의 조화를 위한 최소 지연

    addDebugLog("Authentication initialization delay", delay + "ms");

    const timer = setTimeout(() => {
      addDebugLog("Authentication initialization started");
      initAuth();
    }, delay);

    return () => {
      clearTimeout(timer);
      // 🔧 [Pi 동의 화면 대기 개선] 재시도 타이머 정리
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, piEnvironment, isEnvReady]); // 🔧 isEnvReady 의존성 추가

  // 🎯 [간소화] Firebase 익명 인증 제거됨 - 게스트는 Pi 로그인 없이 앱 사용
  // 비용 절감 및 로직 단순화를 위해 익명 인증 기능 완전 제거

  // 🔧 [실행 순서 개선] 점검 모드 체크 - 환경 준비 완료 후 실행
  useEffect(() => {
    // 🔧 [iOS Safari Fix] 타임아웃 설정 - iOS Safari에서 무한 대기 방지
    const maintenanceTimeout = setTimeout(() => {
      if (isCheckingMaintenance) {
        addDebugLog('[App] ⚠️ 점검 모드 체크 타임아웃 - 정상 서비스로 처리');
        setIsCheckingMaintenance(false);
        setMaintenanceMode(false);
      }
    }, isIOSSafari ? 5000 : 10000); // iOS Safari는 5초, 그 외는 10초

    // 환경이 준비되지 않았으면 실행하지 않음
    if (!isEnvReady || !piEnvironment) {
      addDebugLog('[App] 환경 미확정 - 점검 모드 체크 대기');
      // 🔧 [iOS Safari Fix] 환경이 너무 오래 준비되지 않으면 강제로 진행
      const envTimeout = setTimeout(() => {
        if (!isEnvReady || !piEnvironment) {
          addDebugLog('[App] ⚠️ 환경 초기화 타임아웃 - 기본값으로 진행');
          setIsCheckingMaintenance(false);
          setMaintenanceMode(false);
        }
      }, isIOSSafari ? 8000 : 15000);
      return () => clearTimeout(envTimeout);
    }

    const checkMaintenanceFromFirestore = async () => {
      try {
        addDebugLog('[App] 🎯 비용 최적화: Firestore에서 점검 모드 상태 직접 확인 시작');

        // 현재 환경에 맞는 문서 경로 구성
        const configDocPath = `system_config_${piEnvironment}`;

        addDebugLog('[App] 점검 모드 문서 경로:', configDocPath);

        // Firestore에서 시스템 설정 문서 직접 읽기
        const configDocRef = doc(db, 'cache', configDocPath);
        // 🔧 [iOS Safari Fix] iOS Safari에서 타임아웃 적용
        const configDocSnap = await Promise.race([
          getDoc(configDocRef),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Firestore timeout')), isIOSSafari ? 3000 : 5000)
          )
        ]);

        if (configDocSnap.exists()) {
          const config = configDocSnap.data();
          addDebugLog('[App] Firestore에서 점검 모드 설정 읽기 성공:', config);

          // 만료 시간 확인
          const now = new Date();
          const expiresAt = config.expires_at?.toDate();

          if (expiresAt && now > expiresAt) {
            // 만료된 경우 - 정상 서비스로 처리
            addDebugLog('[App] 점검 모드 설정이 만료됨 - 정상 서비스로 처리');
            setMaintenanceMode(false);
          } else {
            // 유효한 설정
            if (config.maintenance_mode) {
              addDebugLog('[App] ✅ 점검 모드 활성화됨');
              setMaintenanceMode(true);
              setMaintenanceMessage(config.maintenance_message || t('maintenance_title'));
            } else {
              addDebugLog('[App] ✅ 정상 서비스 중');
              setMaintenanceMode(false);
            }
          }
        } else {
          // 문서가 없으면 정상 서비스로 처리
          addDebugLog('[App] 점검 모드 설정 문서 없음 - 정상 서비스로 처리');
          setMaintenanceMode(false);
        }

        addDebugLog('[App] ✅ 비용 최적화 성공: Functions 호출 0회로 점검 모드 확인 완료');

      } catch (error) {
        addDebugLog('[App] Firestore 점검 모드 확인 실패:', error.message);

        // [안전장치] 에러 발생 시 정상 서비스로 기본값 설정 (사용자 경험 우선)
        setMaintenanceMode(false);
        addDebugLog('[App] ⚠️ 네트워크 오류 시 안전한 기본값 적용: 정상 서비스');
      } finally {
        clearTimeout(maintenanceTimeout);
        setIsCheckingMaintenance(false);
      }
    };

    checkMaintenanceFromFirestore();

    return () => {
      clearTimeout(maintenanceTimeout);
    };
  }, [piEnvironment, isEnvReady, t, isIOSSafari, isCheckingMaintenance]); // 🔧 환경 준비 완료 후 실행

  // 🔧 [Pi 동의 화면 대기 개선] 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  // 🔧 [iOS Safari Fix] 환경 초기화 실패 시 fallback 처리
  // 주의: 이는 최후의 안전장치로, determineEnvironment의 타임아웃과 중복될 수 있음
  useEffect(() => {
    if (!isEnvReady || !piEnvironment) {
      const timer = setTimeout(() => {
        if (!isEnvReady || !piEnvironment) {
          addDebugLog('[App] ⚠️ 강제 렌더링 - 환경 초기화 실패 시 fallback');
          // getEnvironment()를 사용하여 올바른 기본값 보장 (도메인 기반 판단)
          const fallbackEnv = getEnvironment();
          setPiEnvironment(fallbackEnv);
          setIsEnvReady(true);
          setIsCheckingMaintenance(false);
          setForceRender(true);
        }
      }, isIOSSafari ? 10000 : 20000); // determineEnvironment 타임아웃보다 길게 설정
      return () => clearTimeout(timer);
    }
  }, [isEnvReady, piEnvironment, isIOSSafari]);

  if (!showSplash && maintenanceMode) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f0f2f5',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <h1 style={{ color: '#e74c3c', marginBottom: '1rem' }}>⚠️ {t('maintenance_title')}</h1>
        <p style={{ fontSize: '1.2rem', marginBottom: '2rem', lineHeight: 1.6 }}>
          {maintenanceMessage}
        </p>
        <p style={{ color: '#666', fontSize: '0.9rem' }}>
          {t('maintenance_notice')}
        </p>
      </div>
    );
  }

  // 🎯 [간소화] 로그인 화면 제거됨 - 기본값이 'guest'이므로 불필요
  // authState === 'unauthenticated' 조건은 더 이상 사용되지 않음
  // SpotGoMain.js의 Pi Login 버튼이 수동 로그인 담당

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      {!showSplash && (
      <>
      {/* 🎯 [추가] 뒤로 가기 종료 방지 토스트 */}
      {showExitToast && (
        <div style={{
          position: 'fixed',
          bottom: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '20px',
          zIndex: 10000,
          fontSize: '14px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
          transition: 'opacity 0.3s',
          pointerEvents: 'none',
          whiteSpace: 'nowrap'
        }}>
          {t('press_back_again_to_exit', '한 번 더 누르면 종료됩니다')}
        </div>
      )}

      {/* 🎯 [간소화] 테스트넷 환경 표시 배너 */}
      {(piEnvironment === 'testnet' || piEnvironment === 'sandbox') && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: '#FF6B6B',
          color: 'white',
          textAlign: 'center',
          padding: '4px 0',
          fontSize: '12px',
          fontWeight: '700',
          letterSpacing: '2px',
          zIndex: 99999
        }}>
          TESTNET
        </div>
      )}
      <div style={{ paddingTop: (piEnvironment === 'testnet' || piEnvironment === 'sandbox') ? '24px' : '0' }}>
        <ErrorBoundary> {/* 🎯 [iOS Fix] 에러 발생 시 빈 화면 대신 에러 메시지 표시 */}
          <AppProvider environment={piEnvironment || getEnvironment()} piUser={piUser} authState={authState}> {/* 🎯 [근본 해결] authState 전달, 🔧 [iOS Safari Fix] getEnvironment()로 올바른 기본값 보장 */}
            {/* ✅ [Admin] 독립 Admin 페이지 - Main 스킵 */}
            {isAdminMode ? (
              <Suspense fallback={<LoadingScreen />}>
                <AdminPage piUser={piUser} />
              </Suspense>
            ) : (
              <>
                {/* 🎯 [ToS] 로그인 시 약관 동의 체크 */}
                <TosAgreementModal piUser={piUser} />
                <MinigameProvider>
                  <GamePlayProvider>
                    <ThemeDownloadProvider>
                      <DonationProvider piUser={piUser}>
                        <CityEquippedCacheProvider environment={piEnvironment} selfUserId={piUser?.uid}> {/* 🎯 [CityCache] Provider 추가 */}
                          <Suspense fallback={<LoadingScreen />}>
                            <Routes>
                              <Route path="/" element={<Navigate to="/main" replace />} />
                              <Route path="/main" element={<Main piUser={piUser} platform={platform} />} />
                              <Route path="/game/:stageId" element={<GameWrapper />} />
                              <Route path="/game" element={<Navigate to="/main" replace />} />
                              <Route path="/result" element={<GameResult />} />
                              <Route path="/donation-register" element={<DonationRegister />} /> {/* [추가] 후원 등록 페이지 */}
                              <Route path="/seller" element={<SellerDashboard />} /> {/* [NEW] Seller Route */}
                              <Route path="/minigame/*" element={
                                <Suspense fallback={<MinigameLoadingScreen />}>
                                  <MinigameRouter />
                                </Suspense>
                              } /> {/* [추가] 미니게임 라우팅 - 동적 로드 */}
                              <Route path="/lobby" element={<Navigate to="/main" replace />} />
                              <Route path="/play" element={<Navigate to="/game" replace />} />
                              <Route path="*" element={<Navigate to="/main" replace />} />
                            </Routes>
                          </Suspense>
                        </CityEquippedCacheProvider>
                      </DonationProvider>
                    </ThemeDownloadProvider>
                  </GamePlayProvider>
                </MinigameProvider>
              </>
            )}
            {/* 🎯 [UX] 글로벌 로딩 오버레이 */}
            {loadingOverlay.show && !bootCover && (
              <LoadingOverlay message={loadingOverlay.message} />
            )}
          </AppProvider>
        </ErrorBoundary> {/* 🎯 [iOS Fix] ErrorBoundary 닫기 */}
      </div>
      </>
      )}
      {(showSplash || bootCover) && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100000 }}>
          <SplashScreen />
        </div>
      )}
      {!showSplash && !bootCover && authState === 'initializing' && (
        <div style={{
          position: 'fixed',
          top: '12px',
          right: '12px',
          zIndex: 100001,
          background: 'rgba(0, 0, 0, 0.72)',
          color: '#fff',
          fontSize: '12px',
          padding: '6px 10px',
          borderRadius: '999px',
          pointerEvents: 'none'
        }}>
          {t('login_in_progress')}
        </div>
      )}
    </>
  );
}

export default App;
